<script>
  // LayoutEditorModal — WYSIWYG editor for the CC preview grid config.
  //
  // Left pane: labeled numeric inputs for canvas dims, padding, grid
  // cell size, spacing, and connectivity toggle dimensions.
  // Right pane: live SVG preview at ~40% scale showing every element
  // with its label + (x,y,w,h) — hover to see coords.
  //
  // The server is the source of truth for the CURRENT saved config;
  // typing into a field applies changes LOCALLY (updates preview) but
  // does NOT persist until Save. Reset restores server defaults.
  //
  // The saved config feeds the ``cc_preview`` prompt template's
  // {canvas_w}/{canvas_h}/{grid_summary}/{layout_table} placeholders,
  // so what you see here is exactly what the model gets told.
  import { onMount } from "svelte";
  import { ccPreviewLayoutApi } from "../api/client.js";

  let { onClose } = $props();

  // Server-side snapshot (what's saved). Populated on mount.
  let saved = $state(null);

  // Local editable config — a copy of `saved.config` that the user
  // mutates via inputs. When identical to `saved.config` the Save
  // button is disabled.
  let cfg = $state(null);

  let loading = $state(true);
  let error = $state(null);
  let saveError = $state(null);
  let busy = $state(false);
  let msg = $state("");

  async function load() {
    loading = true;
    error = null;
    try {
      saved = await ccPreviewLayoutApi.get();
      cfg = { ...saved.config };
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  }

  onMount(load);

  // Compute layout LIVE from the local cfg by calling the server's PUT?
  // No — that would round-trip on every keystroke. Instead we call the
  // GET endpoint once and reuse the server's compute logic via a
  // dedicated preview call. Simpler: reuse the same server compute by
  // POSTing dry-run… also overkill. Cleanest for MVP: reimplement the
  // math client-side (kept intentionally small — mirrors the Python
  // module for the ~30 elements the model needs).
  const computed = $derived(cfg ? computeLayout(cfg) : null);
  const dirty = $derived(
    cfg && saved && JSON.stringify(cfg) !== JSON.stringify(saved.config)
  );

  function computeLayout(c) {
    const colX = (i) => c.padding_h + i * (c.grid_cell + c.grid_spacing);
    const spanW = (n) => n * c.grid_cell + (n - 1) * c.grid_spacing;
    const spanH = (n) => n * c.grid_cell + (n - 1) * c.grid_spacing;
    const cols = Array.from({ length: c.grid_cols }, (_, i) => colX(i));

    const rowH = c.grid_cell;
    const rowH2 = spanH(2);
    const yRow1 = c.padding_top;
    const yRow2 = yRow1 + rowH2 + c.grid_spacing;
    const yRow3 = yRow2 + rowH + c.grid_spacing;
    const yRow4 = yRow3 + rowH + c.grid_spacing;
    const yRow5 = yRow4 + rowH + c.grid_spacing;
    const yRow6 = yRow5 + rowH + c.grid_spacing;
    const yEnd = yRow6 + rowH;

    const tileWH = spanW(2);
    const sliderH = spanH(2);

    // Toggle positions inside connectivity tile
    const togXL = cols[0] + c.toggle_pad;
    const togXR = togXL + c.toggle_size + c.toggle_spacing;
    const togYT = yRow1 + c.toggle_pad;
    const togYB = togYT + c.toggle_size + c.toggle_spacing;

    const revealY0 = yEnd + c.grid_spacing;
    const revealY1 = c.canvas_h;
    const revealH = Math.max(0, revealY1 - revealY0);
    const revealPct = c.canvas_h ? Math.round((revealH / c.canvas_h) * 1000) / 10 : 0;

    const els = [];
    // Status bar
    const stY = Math.max(Math.floor(c.padding_top / 2) - 25, 40);
    els.push({ id: "1",  name: "SIGNAL_BARS",      x: c.padding_h,      y: stY, w: 80,  h: 50, kind: "status" });
    els.push({ id: "2",  name: "WIFI_GLYPH",       x: c.padding_h + 92, y: stY, w: 60,  h: 50, kind: "status" });
    els.push({ id: "3",  name: "TEXT_100_PERCENT", x: c.canvas_w - c.padding_h - 100,       y: stY, w: 90,  h: 50, kind: "status" });
    els.push({ id: "4",  name: "BATTERY_ICON",     x: c.canvas_w - c.padding_h,             y: stY, w: 100, h: 50, kind: "status" });
    // Row 1 tiles
    els.push({ id: "5",  name: "CONNECTIVITY_TILE", x: cols[0], y: yRow1, w: tileWH, h: tileWH, kind: "tile" });
    els.push({ id: "5a", name: "AIRPLANE_TOGGLE",  x: togXL, y: togYT, w: c.toggle_size, h: c.toggle_size, kind: "toggle" });
    els.push({ id: "5b", name: "CELLULAR_TOGGLE",  x: togXR, y: togYT, w: c.toggle_size, h: c.toggle_size, kind: "toggle" });
    els.push({ id: "5c", name: "WIFI_TOGGLE",      x: togXL, y: togYB, w: c.toggle_size, h: c.toggle_size, kind: "toggle" });
    els.push({ id: "5d", name: "BLUETOOTH_TOGGLE", x: togXR, y: togYB, w: c.toggle_size, h: c.toggle_size, kind: "toggle" });
    els.push({ id: "6",  name: "MEDIA_TILE",       x: cols[2], y: yRow1, w: tileWH, h: tileWH, kind: "tile" });
    // Row 2 decoration
    for (let i = 0; i < c.grid_cols; i++) {
      els.push({ id: `${7 + i}`, name: `VIGNETTE_${i + 1}`, x: cols[i], y: yRow2, w: c.grid_cell, h: c.grid_cell, kind: "circle" });
    }
    // Row 3 focus
    els.push({ id: "11", name: "FOCUS_PILL",    x: cols[0], y: yRow3, w: spanW(2),      h: c.grid_cell, kind: "pill" });
    els.push({ id: "12", name: "SILENT_CIRCLE", x: cols[2], y: yRow3, w: c.grid_cell,   h: c.grid_cell, kind: "circle" });
    els.push({ id: "13", name: "LOCK_CIRCLE",   x: cols[3], y: yRow3, w: c.grid_cell,   h: c.grid_cell, kind: "circle" });
    // Rows 4-5 apps + sliders
    els.push({ id: "14", name: "FLASHLIGHT_CIRCLE", x: cols[0], y: yRow4, w: c.grid_cell, h: c.grid_cell, kind: "circle" });
    els.push({ id: "15", name: "ALARM_CIRCLE",      x: cols[1], y: yRow4, w: c.grid_cell, h: c.grid_cell, kind: "circle" });
    els.push({ id: "16", name: "CALCULATOR_CIRCLE", x: cols[0], y: yRow5, w: c.grid_cell, h: c.grid_cell, kind: "circle" });
    els.push({ id: "17", name: "CAMERA_CIRCLE",     x: cols[1], y: yRow5, w: c.grid_cell, h: c.grid_cell, kind: "circle" });
    els.push({ id: "18", name: "VOLUME_SLIDER",     x: cols[2], y: yRow4, w: c.grid_cell, h: sliderH, kind: "pill" });
    els.push({ id: "19", name: "BRIGHTNESS_SLIDER", x: cols[3], y: yRow4, w: c.grid_cell, h: sliderH, kind: "pill" });
    // Row 6 utility
    const utilNames = ["RECORD_DOT_CIRCLE", "GEAR_CIRCLE", "MIC_CIRCLE", "EXPAND_CIRCLE"];
    for (let i = 0; i < c.grid_cols; i++) {
      els.push({ id: `${20 + i}`, name: utilNames[i], x: cols[i], y: yRow6, w: c.grid_cell, h: c.grid_cell, kind: "circle" });
    }

    return {
      cols,
      rows: { yRow1, yRow2, yRow3, yRow4, yRow5, yRow6, yEnd },
      elements: els,
      reveal: { y0: revealY0, y1: revealY1, height: revealH, pct: revealPct },
    };
  }

  // ----- Save / reset ---------------------------------------------------
  async function save() {
    if (!dirty || busy) return;
    busy = true;
    saveError = null;
    msg = "Đang lưu...";
    try {
      saved = await ccPreviewLayoutApi.update(cfg);
      cfg = { ...saved.config };
      msg = "✓ Đã lưu (job mới sẽ dùng layout này)";
    } catch (err) {
      saveError = err.message;
      msg = "";
    } finally {
      busy = false;
    }
  }

  async function reset() {
    if (busy) return;
    if (!confirm("Reset layout về default? (canvas 804×1748, cell 139, spacing 24...)")) return;
    busy = true;
    saveError = null;
    msg = "Đang reset...";
    try {
      saved = await ccPreviewLayoutApi.reset();
      cfg = { ...saved.config };
      msg = "✓ Đã reset về default";
    } catch (err) {
      saveError = err.message;
      msg = "";
    } finally {
      busy = false;
    }
  }

  function revertLocal() {
    if (saved) cfg = { ...saved.config };
    msg = "Đã hủy edit local";
  }

  function handleKey(e) { if (e.key === "Escape") onClose?.(); }

  // ----- SVG geometry ---------------------------------------------------
  const SVG_MAX_W = 380;
  const scale = $derived(cfg ? SVG_MAX_W / cfg.canvas_w : 1);
  const svgW = $derived(cfg ? cfg.canvas_w * scale : 0);
  const svgH = $derived(cfg ? cfg.canvas_h * scale : 0);

  // Element kind → SVG color / fill
  function kindStyle(kind) {
    switch (kind) {
      case "tile":   return { fill: "rgba(124,158,255,0.15)", stroke: "#7c9eff", strokeWidth: 1.5 };
      case "circle": return { fill: "rgba(76,209,151,0.15)",  stroke: "#4cd197", strokeWidth: 1.5 };
      case "pill":   return { fill: "rgba(255,193,84,0.15)",  stroke: "#ffc154", strokeWidth: 1.5 };
      case "toggle": return { fill: "rgba(255,133,133,0.20)", stroke: "#ff8585", strokeWidth: 1 };
      case "status": return { fill: "rgba(180,180,180,0.15)", stroke: "#888",    strokeWidth: 1 };
      default:       return { fill: "rgba(200,200,200,0.10)", stroke: "#666",    strokeWidth: 1 };
    }
  }

  // Whether element renders rounded (pills + circles) or square (tiles/status)
  function isRounded(kind) {
    return kind === "circle" || kind === "pill" || kind === "toggle";
  }
</script>

<svelte:window onkeydown={handleKey} />

<div class="modal-backdrop" onclick={onClose} role="presentation">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="layout-modal-title" tabindex="-1"
       onclick={(e) => e.stopPropagation()}
       onkeydown={(e) => e.stopPropagation()}>
    <header class="modal-head">
      <h2 id="layout-modal-title">📐 CC Preview Layout Editor <span class="muted">(global config)</span></h2>
      <div class="modal-actions">
        <button class="link-btn" onclick={onClose}>Đóng (Esc)</button>
      </div>
    </header>

    {#if loading}
      <div class="loading">Đang tải config...</div>
    {:else if error}
      <div class="error">Lỗi: {error}</div>
    {:else if cfg && computed}
      <div class="modal-body">
        <!-- Left pane: controls -->
        <aside class="controls">
          <section>
            <h3>Canvas</h3>
            <div class="row">
              <label>canvas_w
                <input type="number" bind:value={cfg.canvas_w} min="200" max="4000" />
              </label>
              <label>canvas_h
                <input type="number" bind:value={cfg.canvas_h} min="200" max="6000" />
              </label>
            </div>
          </section>

          <section>
            <h3>Padding</h3>
            <div class="row">
              <label>padding_h <span class="hint">left/right</span>
                <input type="number" bind:value={cfg.padding_h} min="0" max="500" />
              </label>
              <label>padding_top <span class="hint">status bar</span>
                <input type="number" bind:value={cfg.padding_top} min="0" max="800" />
              </label>
            </div>
          </section>

          <section>
            <h3>Grid</h3>
            <div class="row">
              <label>grid_cell <span class="hint">1×1 size</span>
                <input type="number" bind:value={cfg.grid_cell} min="50" max="400" />
              </label>
              <label>grid_spacing <span class="hint">gap</span>
                <input type="number" bind:value={cfg.grid_spacing} min="0" max="100" />
              </label>
              <label>grid_cols
                <input type="number" bind:value={cfg.grid_cols} min="2" max="8" />
              </label>
            </div>
          </section>

          <section>
            <h3>Corner radius</h3>
            <div class="row">
              <label>tile_radius <span class="hint">Big tiles</span>
                <input type="number" bind:value={cfg.tile_radius} min="0" max="150" />
              </label>
              <label>pill_radius <span class="hint">Focus pill + sliders</span>
                <input type="number" bind:value={cfg.pill_radius} min="0" max="200" />
              </label>
            </div>
          </section>

          <section>
            <h3>Connectivity toggle <span class="hint muted">(inside 2×2 tile)</span></h3>
            <div class="row">
              <label>toggle_size
                <input type="number" bind:value={cfg.toggle_size} min="30" max="250" />
              </label>
              <label>toggle_pad
                <input type="number" bind:value={cfg.toggle_pad} min="0" max="80" />
              </label>
              <label>toggle_spacing
                <input type="number" bind:value={cfg.toggle_spacing} min="0" max="60" />
              </label>
            </div>
            {#if cfg.grid_cell > 0}
              {@const tileWH = cfg.grid_cell * 2 + cfg.grid_spacing}
              {@const derived = Math.round((tileWH - cfg.toggle_pad * 2 - cfg.toggle_spacing) / 2)}
              <p class="small muted">
                Sanity check: (tile 2×2 = {tileWH}) - 2×{cfg.toggle_pad} pad - {cfg.toggle_spacing} spacing / 2
                = <strong>{derived}</strong> — {derived === cfg.toggle_size ? '✓ khớp toggle_size' : `⚠ ≠ toggle_size (${cfg.toggle_size})`}
              </p>
            {/if}
          </section>

          <section class="derived">
            <h3>Derived (auto)</h3>
            <div class="derived-grid">
              <span>Content bounds:</span>
              <code>x={cfg.padding_h}..{cfg.canvas_w - cfg.padding_h} · y={cfg.padding_top}..{computed.rows.yEnd}</code>
              <span>Column x:</span>
              <code>{computed.cols.join(", ")}</code>
              <span>Row y-tops:</span>
              <code>{computed.rows.yRow1}, {computed.rows.yRow2}, {computed.rows.yRow3}, {computed.rows.yRow4}, {computed.rows.yRow5}, {computed.rows.yRow6}</code>
              <span>Reveal:</span>
              <code>y={computed.reveal.y0}..{computed.reveal.y1} ({computed.reveal.height} px, {computed.reveal.pct}%)</code>
            </div>
          </section>

          {#if msg}
            <p class="msg" class:msg-ok={msg.startsWith("✓")}>{msg}</p>
          {/if}
          {#if saveError}
            <p class="msg msg-err">{saveError}</p>
          {/if}

          <div class="save-actions">
            <button class="primary-btn" onclick={save} disabled={!dirty || busy}>
              {busy ? "..." : (dirty ? "💾 Save" : "Saved")}
            </button>
            <button class="link-btn" onclick={revertLocal} disabled={!dirty || busy}>
              Hủy edit
            </button>
            <button class="link-btn danger" onclick={reset} disabled={busy}>
              ↻ Reset defaults
            </button>
          </div>
        </aside>

        <!-- Right pane: SVG preview -->
        <main class="preview">
          <div class="preview-head">
            <h3>Live preview <span class="muted">(scale ≈ {(scale * 100).toFixed(0)}%)</span></h3>
            <div class="legend">
              <span class="lg lg-tile">tile</span>
              <span class="lg lg-circle">circle</span>
              <span class="lg lg-pill">pill</span>
              <span class="lg lg-toggle">toggle</span>
              <span class="lg lg-status">status</span>
            </div>
          </div>
          <div class="svg-wrap">
            <svg width={svgW} height={svgH} viewBox={`0 0 ${cfg.canvas_w} ${cfg.canvas_h}`}
                 xmlns="http://www.w3.org/2000/svg">
              <!-- Canvas outline -->
              <rect x="0" y="0" width={cfg.canvas_w} height={cfg.canvas_h}
                    fill="#0d0f16" stroke="#7c9eff" stroke-width="4" />
              <!-- Content bounds guide -->
              <rect x={cfg.padding_h} y={cfg.padding_top}
                    width={cfg.canvas_w - 2 * cfg.padding_h}
                    height={computed.rows.yEnd - cfg.padding_top}
                    fill="none" stroke="rgba(124,158,255,0.35)" stroke-width="1"
                    stroke-dasharray="8 8" />
              <!-- Reveal area shaded -->
              <rect x="0" y={computed.reveal.y0}
                    width={cfg.canvas_w} height={computed.reveal.height}
                    fill="rgba(76,209,151,0.05)"
                    stroke="rgba(76,209,151,0.4)" stroke-width="1"
                    stroke-dasharray="6 6" />

              <!-- Elements -->
              {#each computed.elements as e (e.id)}
                {@const st = kindStyle(e.kind)}
                {@const r = isRounded(e.kind) ? Math.min(e.w, e.h) / 2 : 0}
                <g>
                  <rect x={e.x} y={e.y} width={e.w} height={e.h}
                        rx={r} ry={r}
                        fill={st.fill}
                        stroke={st.stroke}
                        stroke-width={st.strokeWidth} />
                  <!-- Element id number (top-left corner) -->
                  <text x={e.x + 4} y={e.y + 22}
                        font-family="ui-monospace, monospace"
                        font-size="18" font-weight="700"
                        fill={st.stroke}>{e.id}</text>
                  <title>{e.name}
x={e.x} y={e.y}
w={e.w} h={e.h}</title>
                </g>
              {/each}
            </svg>
          </div>
          <p class="small muted preview-hint">
            Hover 1 element để xem tọa độ. Số ở góc trên-trái = id trong bảng LAYOUT_TABLE của prompt.
          </p>
        </main>
      </div>
    {/if}
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 200;
    padding: 16px;
  }
  .modal {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    width: min(1200px, 100%);
    height: 92vh;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-md);
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 18px;
    border-bottom: 1px solid var(--border);
  }
  .modal-head h2 {
    margin: 0;
    font-size: 1rem;
    color: var(--text-strong);
  }
  .modal-head .muted { font-weight: 400; font-size: 0.8rem; }

  .loading, .error {
    padding: 24px;
    color: var(--muted);
  }
  .error { color: #ff8585; }

  .modal-body {
    display: grid;
    grid-template-columns: minmax(360px, 480px) 1fr;
    flex: 1;
    min-height: 0;
  }

  .controls {
    border-right: 1px solid var(--border);
    overflow-y: auto;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .controls section h3 {
    margin: 0 0 6px;
    font-size: 0.78rem;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
  .controls label {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 0.75rem;
    color: var(--muted-strong);
  }
  .controls label input {
    padding: 5px 8px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
    color: var(--text);
    font-family: ui-monospace, monospace;
    font-size: 0.88rem;
  }
  .hint {
    font-size: 0.68rem;
    color: var(--muted);
    font-weight: normal;
  }
  .small { font-size: 0.72rem; margin: 2px 0 0; }

  .derived {
    background: var(--panel-2);
    border-radius: var(--radius-sm);
    padding: 10px 12px;
  }
  .derived-grid {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 6px 12px;
    font-size: 0.75rem;
  }
  .derived-grid span { color: var(--muted); }
  .derived-grid code {
    font-family: ui-monospace, monospace;
    color: var(--text);
    background: var(--bg);
    padding: 2px 6px;
    border-radius: 3px;
  }

  .msg {
    margin: 6px 0 0;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.78rem;
    background: var(--panel-2);
  }
  .msg-ok  { color: #4cd197; }
  .msg-err { color: #ff8585; }

  .save-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .danger { color: #ff8585; }

  /* Right pane */
  .preview {
    display: flex;
    flex-direction: column;
    padding: 14px 18px;
    overflow: auto;
  }
  .preview-head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 8px;
  }
  .preview-head h3 {
    margin: 0;
    font-size: 0.9rem;
    color: var(--text-strong);
  }
  .legend { display: flex; gap: 6px; flex-wrap: wrap; }
  .lg {
    font-size: 0.7rem; padding: 2px 8px; border-radius: 999px;
    border: 1px solid var(--border);
  }
  .lg-tile   { border-color: #7c9eff; color: #7c9eff; }
  .lg-circle { border-color: #4cd197; color: #4cd197; }
  .lg-pill   { border-color: #ffc154; color: #ffc154; }
  .lg-toggle { border-color: #ff8585; color: #ff8585; }
  .lg-status { border-color: #888;    color: #999;    }

  .svg-wrap {
    display: flex;
    justify-content: center;
    background: var(--panel-2);
    padding: 14px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
  }
  .preview-hint { text-align: center; margin-top: 8px; }

  @media (max-width: 900px) {
    .modal-body {
      grid-template-columns: 1fr;
    }
    .controls {
      border-right: none;
      border-bottom: 1px solid var(--border);
      max-height: 40vh;
    }
  }
</style>
